// components/ReportarModal.js
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from "@mui/material";
import { Close, CloudUpload, BugReport, Lightbulb, NewReleases, AttachFile } from "@mui/icons-material";
import { createBacklogItem } from "../services/backlogService";

const ReportarModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    tipo: "Error",
    titulo: "",
    descripcion: "",
  });
  const [archivo, setArchivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const tiposReporte = [
    { value: "Error", label: "Error", icon: <BugReport />, color: "#d32f2f" },
    { value: "Mejora", label: "Mejora", icon: <Lightbulb />, color: "#f57c00" },
    { value: "Nueva Funcionalidad", label: "Nueva Funcionalidad", icon: <NewReleases />, color: "#1976d2" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tamaño (5MB máx)
      if (file.size > 5 * 1024 * 1024) {
        setError("El archivo no puede superar los 5MB");
        return;
      }
      setArchivo(file);
      setError(null);
    }
  };

  const handleRemoveFile = () => {
    setArchivo(null);
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!formData.titulo.trim()) {
      setError("El título es obligatorio");
      return;
    }
    if (!formData.descripcion.trim()) {
      setError("La descripción es obligatoria");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createBacklogItem({
        tipo: formData.tipo,
        titulo: formData.titulo.trim(),
        descripcion: formData.descripcion.trim(),
        archivo: archivo,
      });

      setSuccess(true);

      // Llamar al callback onSuccess si existe
      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        handleCloseModal();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Error al enviar el reporte");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      tipo: "Error",
      titulo: "",
      descripcion: "",
    });
    setArchivo(null);
    setError(null);
    setSuccess(false);
    onClose();
  };

  const getIconForTipo = (tipo) => {
    const tipoData = tiposReporte.find((t) => t.value === tipo);
    return tipoData ? tipoData.icon : <BugReport />;
  };

  const getColorForTipo = (tipo) => {
    const tipoData = tiposReporte.find((t) => t.value === tipo);
    return tipoData ? tipoData.color : "#d32f2f";
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {getIconForTipo(formData.tipo)}
            <Typography variant="h6" component="span">
              Reportar {formData.tipo}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseModal} size="small" disabled={loading}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            ¡Reporte enviado exitosamente! Los administradores serán notificados.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 2 }} />

            {/* Tipo de Reporte */}
            <TextField
              select
              fullWidth
              label="Tipo de Reporte"
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {tiposReporte.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {React.cloneElement(tipo.icon, { sx: { color: tipo.color, fontSize: 20 } })}
                    <Typography>{tipo.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            {/* Título */}
            <TextField
              fullWidth
              label="Título"
              name="titulo"
              value={formData.titulo}
              onChange={handleChange}
              disabled={loading}
              placeholder="Resumen breve del problema o sugerencia"
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.titulo.length}/200 caracteres`}
              sx={{ mb: 2 }}
            />

            {/* Descripción */}
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Descripción"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              disabled={loading}
              placeholder={
                formData.tipo === "Error"
                  ? "Describe el error: ¿qué pasó?, ¿qué esperabas que pasara?, ¿cómo reproducirlo?"
                  : formData.tipo === "Mejora"
                  ? "Describe la mejora: ¿qué cambiarías?, ¿por qué sería mejor?"
                  : "Describe la funcionalidad: ¿qué te gustaría que se agregara?, ¿para qué la usarías?"
              }
              sx={{ mb: 2 }}
            />

            {/* Archivo Adjunto */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Adjuntar Archivo (opcional)
              </Typography>
              <Box
                sx={{
                  border: `2px dashed ${alpha(getColorForTipo(formData.tipo), 0.3)}`,
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  backgroundColor: alpha(getColorForTipo(formData.tipo), 0.05),
                  cursor: archivo ? "default" : "pointer",
                  transition: "all 0.2s",
                  "&:hover": archivo
                    ? {}
                    : {
                        borderColor: alpha(getColorForTipo(formData.tipo), 0.5),
                        backgroundColor: alpha(getColorForTipo(formData.tipo), 0.1),
                      },
                }}
                onClick={() => !archivo && !loading && document.getElementById("file-upload").click()}
              >
                {archivo ? (
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AttachFile fontSize="small" />
                      <Typography variant="body2">{archivo.name}</Typography>
                      <Chip
                        label={`${(archivo.size / 1024).toFixed(1)} KB`}
                        size="small"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Box>
                    <IconButton size="small" onClick={handleRemoveFile} disabled={loading}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box>
                    <CloudUpload sx={{ fontSize: 40, color: getColorForTipo(formData.tipo), mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Click para adjuntar una imagen o documento
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Máx. 5MB - JPG, PNG, GIF, PDF, TXT
                    </Typography>
                  </Box>
                )}
              </Box>
              <input
                id="file-upload"
                type="file"
                hidden
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain"
                onChange={handleFileChange}
                disabled={loading}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
              Tu reporte será enviado a los administradores y recibirás una notificación cuando sea respondido.
            </Typography>
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading} startIcon={loading && <CircularProgress size={20} />}>
            {loading ? "Enviando..." : "Enviar Reporte"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ReportarModal;
